/**
 * §A.6 · Las plantillas de grados que los clubes clonan.
 *
 * ## `used_by` cuenta las dos formas de seguir una plantilla
 *
 * Adoptada y **clonada con el mismo código**: «contar solo la primera daría un cero
 * tranquilizador sobre un sistema que en realidad siguen ochenta clubes por copia».
 * Es la cifra que decide si tocar esta escalera es una corrección o una bomba.
 *
 * ## El Hapkido es el que más cuidado pide
 *
 * `DDBB.md` §7: «varía bastante más entre escuelas que el de TaeKwonDo o el de
 * Karate, y lo que hay cargado es una progresión razonable, no un estándar. Hay que
 * contrastarlo con la federación del primer club de Hapkido que entre». Eso sale en
 * pantalla, porque es un aviso que caduca y hay que ver cuándo se cumple.
 *
 * ## Los cinturones se sustituyen enteros, y por eso no se editan aquí
 *
 * `PUT /ranks` cambia la secuencia completa: «un sistema de grados **es una
 * secuencia**; cambiar un cinturón de sitio es cambiar el orden de todos, y hacerlo
 * de uno en uno pasa por estados donde dos comparten posición».
 *
 * Esta pantalla **enseña** la escalera y deja corregir nombres y colores por
 * posición, que es el caso real: una errata en «Amarrillo». Lo que no ofrece es
 * añadir, quitar o reordenar, y se dice por qué: son 40 filas y un editor de
 * secuencias completo es otra pantalla. Lo que se corrige aquí conserva el
 * identificador de cada fila, así que **corrige el grado que ya lleva el alumno** en
 * lugar de dejárselo huérfano.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { cuenta } from '@/shared/ui/formato';
import { Cinturon } from '@/club/grados/Cinturon';
import type {
  CinturonGlobal,
  PlantillaDeGrados,
} from '@/backoffice/tiposDeCatalogos';

/** Lo editable de un cinturón: el nombre y los dos colores. */
type Correccion = Record<
  string,
  { name: string; color_hex: string; color_hex_2: string }
>;

export function CatalogoDeGrados() {
  const cache = useQueryClient();
  const [abierta, setAbierta] = useState<string | null>(null);
  const [correcciones, setCorrecciones] = useState<Correccion>({});
  const [problema, setProblema] = useState<Problema | null>(null);

  const plantillas = useQuery({
    queryKey: ['admin', 'plantillas'],
    queryFn: async () => {
      const { data, error } = await api.v1.admin['belt-schemes'].get({
        query: {},
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as PlantillaDeGrados[];
    },
  });

  const corregir = useMutation({
    mutationFn: async (p: PlantillaDeGrados) => {
      /*
       * Se manda **la escalera entera**, con la corrección aplicada donde la haya.
       * El servidor actualiza por posición y conserva los identificadores, así que
       * esto corrige y no recrea.
       */
      const ranks = p.ranks.map((c) => {
        const cambio = correcciones[c.id];
        return {
          name: cambio ? cambio.name.trim() : c.name,
          short_name: c.short_name,
          color_hex: cambio ? cambio.color_hex : c.color_hex,
          color_hex_2: cambio
            ? cambio.color_hex_2 === ''
              ? null
              : cambio.color_hex_2
            : c.color_hex_2,
          sort_order: c.sort_order,
          max_stripes: c.max_stripes,
          is_dan: c.is_dan,
          min_months_since_last: c.min_months_since_last,
          min_attendance_rate: c.min_attendance_rate,
          min_classes: c.min_classes,
          min_age: c.min_age,
          max_age: c.max_age,
        };
      });

      const r = await api.v1.admin['belt-schemes']({ id: p.id }).ranks.put({
        ranks,
      });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setCorrecciones({});
      void cache.invalidateQueries({ queryKey: ['admin', 'plantillas'] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  if (plantillas.isPending) return <Cargando filas={5} etiqueta="Cargando" />;

  if (plantillas.isError || !plantillas.data) {
    return (
      <PanelError
        problema={aProblema(plantillas.error)}
        reintentar={() => void plantillas.refetch()}
      />
    );
  }

  const valorDe = (c: CinturonGlobal) =>
    correcciones[c.id] ?? {
      name: c.name,
      color_hex: c.color_hex,
      color_hex_2: c.color_hex_2 ?? '',
    };

  const tocado = (p: PlantillaDeGrados) =>
    p.ranks.some((c) => correcciones[c.id] !== undefined);

  return (
    <section>
      <h1>Sistemas de grados</h1>

      <p className="tenue">
        Las plantillas que los clubes clonan.{' '}
        <strong>Corregir una corrige el grado que ya llevan sus alumnos</strong>
        , así que una errata se arregla aquí y se arregla en todos.
      </p>

      {problema && <PanelError problema={problema} />}

      {plantillas.data.length === 0 ? (
        <Vacio
          titulo="No hay ninguna plantilla"
          explicacion="Sin plantillas, cada club que entre se inventará su propia escalera de cinturones y ningún pasaporte se parecerá a otro."
        />
      ) : (
        <ul className="papeles">
          {plantillas.data.map((p) => (
            <li key={p.id}>
              <div className="aviso-cabeza">
                <strong>{p.name}</strong>
                <span className="pastilla pastilla-left">
                  {p.discipline_name}
                </span>
                {p.federation && (
                  <span className="pastilla pastilla-left">{p.federation}</span>
                )}
                <code className="tenue">{p.code}</code>
              </div>

              <p className="tenue">
                {cuenta(p.rank_count, 'cinturón', 'cinturones')} ·{' '}
                {p.used_by === 0
                  ? 'ningún club la sigue todavía'
                  : `${cuenta(p.used_by, 'club la sigue', 'clubes la siguen')}`}
                {/* Adoptada o clonada: las dos cuentan, y por eso la cifra es la que es. */}
              </p>

              {/*
                Aquí había un aviso de «escalera sin contrastar» para el Hapkido.
                Se quitó el 05-10-2026: las cuatro plantillas están contrastadas
                con las normativas de la RFET, la RFEK y la RFEJYDA (ver
                db/migrations/20261005000200).
              */}

              <div className="aviso-pie">
                <span className="tenue">
                  {p.used_by > 0 &&
                    'Cualquier corrección la ven todos sus clubes.'}
                </span>
                <button
                  type="button"
                  className="btn-plano"
                  onClick={() => {
                    setCorrecciones({});
                    setProblema(null);
                    setAbierta(abierta === p.id ? null : p.id);
                  }}
                >
                  {abierta === p.id ? 'Cerrar' : 'Ver la escalera'}
                </button>
              </div>

              {abierta === p.id && (
                <>
                  <div className="tabla-marco">
                    <table className="tabla">
                      <thead>
                        <tr>
                          <th className="num">Nº</th>
                          <th>Cinturón</th>
                          <th>Cómo se llama</th>
                          <th>Color</th>
                          <th>Segundo color</th>
                          <th className="num">Lo llevan</th>
                        </tr>
                      </thead>
                      <tbody>
                        {p.ranks.map((c) => {
                          const v = valorDe(c);
                          return (
                            <tr key={c.id}>
                              <td className="num">{c.sort_order}</td>
                              <td>
                                <Cinturon
                                  color={v.color_hex}
                                  color2={
                                    v.color_hex_2 === '' ? null : v.color_hex_2
                                  }
                                  nombre={v.name}
                                  deDan={c.is_dan}
                                />
                              </td>
                              <td>
                                <input
                                  maxLength={60}
                                  value={v.name}
                                  onChange={(e) =>
                                    setCorrecciones({
                                      ...correcciones,
                                      [c.id]: { ...v, name: e.target.value },
                                    })
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  type="color"
                                  value={v.color_hex}
                                  onChange={(e) =>
                                    setCorrecciones({
                                      ...correcciones,
                                      [c.id]: {
                                        ...v,
                                        color_hex: e.target.value,
                                      },
                                    })
                                  }
                                />
                              </td>
                              <td>
                                {/*
                                  Vacío es «cinturón liso». Un `type="color"` no puede
                                  estar vacío, así que la casilla de al lado es la que
                                  decide si hay segundo color.
                                */}
                                <label className="color-doble">
                                  <input
                                    type="checkbox"
                                    checked={v.color_hex_2 !== ''}
                                    onChange={(e) =>
                                      setCorrecciones({
                                        ...correcciones,
                                        [c.id]: {
                                          ...v,
                                          color_hex_2: e.target.checked
                                            ? '#ffffff'
                                            : '',
                                        },
                                      })
                                    }
                                  />
                                  {v.color_hex_2 !== '' && (
                                    <input
                                      type="color"
                                      value={v.color_hex_2}
                                      onChange={(e) =>
                                        setCorrecciones({
                                          ...correcciones,
                                          [c.id]: {
                                            ...v,
                                            color_hex_2: e.target.value,
                                          },
                                        })
                                      }
                                    />
                                  )}
                                </label>
                              </td>
                              <td className="num">
                                {/*
                                  «Lo llevan catorce» es lo que convierte «cuidado» en
                                  una cifra que se puede valorar.
                                */}
                                {c.students}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <p className="tenue">
                    Aquí se corrigen nombres y colores.{' '}
                    <strong>
                      Añadir, quitar o reordenar cinturones no se hace desde
                      aquí
                    </strong>
                    : una escalera es una secuencia y moverla entera es otra
                    pantalla. Lo que se corrija conserva el grado de cada
                    alumno.
                  </p>

                  {tocado(p) && (
                    <div className="acciones">
                      <button
                        type="button"
                        disabled={corregir.isPending}
                        onClick={() => {
                          setProblema(null);
                          corregir.mutate(p);
                        }}
                      >
                        {corregir.isPending
                          ? 'Guardando…'
                          : p.used_by > 0
                            ? `Corregir en ${cuenta(p.used_by, 'el club que la sigue', 'los ' + String(p.used_by) + ' clubes')}`
                            : 'Guardar la corrección'}
                      </button>
                      <button
                        type="button"
                        className="btn-plano"
                        disabled={corregir.isPending}
                        onClick={() => setCorrecciones({})}
                      >
                        Deshacer
                      </button>
                    </div>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
